import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { formatApiError } from "../context/AuthContext";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Loader2, Plus, Trash2, Save, ArrowUp, ArrowDown, FileSignature, Eye, EyeOff } from "lucide-react";

export default function SubappaltoArticoli() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/settings/subappalto-articles")
      .then((r) => setItems(r.data.map((x) => ({ ...x, _dirty: false }))))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const setField = (id, key, val) => setItems((its) => its.map((x) => (x.id === id ? { ...x, [key]: val, _dirty: true } : x)));

  const saveItem = async (it) => {
    setSavingId(it.id);
    try {
      await api.put(`/settings/subappalto-articles/${it.id}`, { titolo: it.titolo, testo: it.testo, ordine: Number(it.ordine), attivo: it.attivo });
      setItems((its) => its.map((x) => (x.id === it.id ? { ...x, _dirty: false } : x)));
      toast.success("Articolo salvato");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingId(null); }
  };

  const addItem = async () => {
    try {
      await api.post("/settings/subappalto-articles", { titolo: "Nuovo articolo", testo: "" });
      toast.success("Articolo aggiunto");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeItem = async (it) => {
    if (!window.confirm(`Eliminare "${it.titolo}"?`)) return;
    try { await api.delete(`/settings/subappalto-articles/${it.id}`); toast.success("Articolo eliminato"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const move = async (it, dir) => {
    const sorted = [...items].sort((a, b) => a.ordine - b.ordine);
    const idx = sorted.findIndex((x) => x.id === it.id);
    const swap = sorted[idx + dir];
    if (!swap) return;
    const a = Number(it.ordine), b = Number(swap.ordine);
    try {
      await Promise.all([
        api.put(`/settings/subappalto-articles/${it.id}`, { titolo: it.titolo, testo: it.testo, ordine: b, attivo: it.attivo }),
        api.put(`/settings/subappalto-articles/${swap.id}`, { titolo: swap.titolo, testo: swap.testo, ordine: a, attivo: swap.attivo }),
      ]);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sorted = [...items].sort((a, b) => a.ordine - b.ordine);

  return (
    <div className="p-4 lg:p-8 space-y-6 max-w-4xl" data-testid="subappalto-articoli-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <FileSignature className="h-5 w-5 text-primary" />
            <h1 className="text-xl font-heading font-bold">Articoli contratto di subappalto</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Modifica il testo e l'ordine degli articoli, o aggiungine di nuovi. Segnaposto disponibili nel testo:
            <code className="mx-1 px-1 rounded bg-muted text-xs">{"{contratto_n}"}</code>
            <code className="mx-1 px-1 rounded bg-muted text-xs">{"{contratto_data}"}</code>
            <code className="mx-1 px-1 rounded bg-muted text-xs">{"{cantiere}"}</code>
            <code className="mx-1 px-1 rounded bg-muted text-xs">{"{foro}"}</code>.
          </p>
        </div>
        <Button onClick={addItem} data-testid="add-article-btn"><Plus className="h-4 w-4 mr-2" /> Aggiungi articolo</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <div className="space-y-4">
          {sorted.map((it, i) => (
            <Card key={it.id} className={`p-4 space-y-3 ${it.attivo ? "" : "opacity-60"}`} data-testid={`article-card-${it.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex flex-col">
                  <Button size="icon" variant="ghost" className="h-6 w-6" disabled={i === 0} onClick={() => move(it, -1)} data-testid={`article-up-${it.id}`}><ArrowUp className="h-3.5 w-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="h-6 w-6" disabled={i === sorted.length - 1} onClick={() => move(it, 1)} data-testid={`article-down-${it.id}`}><ArrowDown className="h-3.5 w-3.5" /></Button>
                </div>
                <Input className="flex-1 min-w-[200px] font-semibold" value={it.titolo} onChange={(e) => setField(it.id, "titolo", e.target.value)} data-testid={`article-titolo-${it.id}`} />
                {it.tipo === "materiali" && <Badge variant="secondary">Tabella materiali (dinamico)</Badge>}
                {it.tipo === "corrispettivo" && <Badge variant="secondary">Corrispettivo (dinamico)</Badge>}
                <Button size="icon" variant="ghost" onClick={() => setField(it.id, "attivo", !it.attivo)} title={it.attivo ? "Attivo" : "Nascosto"} data-testid={`article-toggle-${it.id}`}>
                  {it.attivo ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4 text-muted-foreground" />}
                </Button>
              </div>
              <Textarea rows={3} value={it.testo || ""} onChange={(e) => setField(it.id, "testo", e.target.value)}
                placeholder={it.tipo === "materiali" ? "Testo introduttivo prima della tabella materiali…" : it.tipo === "corrispettivo" ? "Nota sotto l'importo del corrispettivo…" : "Testo dell'articolo…"}
                data-testid={`article-testo-${it.id}`} />
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">Ordine: {it.ordine}</span>
                <div className="flex gap-2">
                  {it.tipo === "testo" && (
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => removeItem(it)} data-testid={`article-delete-${it.id}`}>
                      <Trash2 className="h-4 w-4 mr-1.5" /> Elimina
                    </Button>
                  )}
                  <Button size="sm" disabled={savingId === it.id || !it._dirty} onClick={() => saveItem(it)} data-testid={`article-save-${it.id}`}>
                    {savingId === it.id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Salva
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
